import type { AgentDto, MetricsDto } from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatCompact, formatDuration, formatNumber, formatPercent } from '@/lib/format';
import { usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { ScoreBar } from './scorers';

const RANGES = {
  '1h': { label: 'Last hour', ms: 3_600_000 },
  '24h': { label: 'Last 24 hours', ms: 86_400_000 },
  '7d': { label: 'Last 7 days', ms: 7 * 86_400_000 },
  '30d': { label: 'Last 30 days', ms: 30 * 86_400_000 },
} as const;
type Range = keyof typeof RANGES;

function Stat({
  label,
  value,
  hint,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  /** The exact figure, on hover, when `value` is abbreviated. */
  title?: string;
}) {
  return (
    <Card className="gap-1 py-4">
      <CardContent className="grid gap-1 px-4">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span
          className="text-2xl font-semibold tabular-nums"
          data-testid={`metric-${label}`}
          title={title}
        >
          {value}
        </span>
        {hint && <span className="text-muted-foreground text-xs">{hint}</span>}
      </CardContent>
    </Card>
  );
}

function bucketLabel(iso: string, bucket: MetricsDto['bucket']) {
  const d = new Date(iso);
  if (bucket === 'minute')
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (bucket === 'hour')
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Vertical bars per time bucket. `segments` stack bottom-up (2px surface gap between them); hovering
 * a column shows its values. One measure per chart: no second axis.
 */
function BarChart({
  label,
  data,
  bucket,
  segments,
  format,
}: {
  label: string;
  data: MetricsDto['series'];
  bucket: MetricsDto['bucket'];
  segments: Array<{
    key: string;
    label: string;
    className: string;
    value: (d: MetricsDto['series'][number]) => number;
  }>;
  format: (n: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const totals = data.map((d) => segments.reduce((sum, s) => sum + s.value(d), 0));
  const max = Math.max(1, ...totals);
  const hovered = hover === null ? null : data[hover];
  return (
    <div className="grid gap-2">
      {segments.length > 1 && (
        <ul className="text-muted-foreground flex gap-4 text-xs" aria-label={`${label} legend`}>
          {segments.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className={cn('size-2.5 rounded-sm', s.className)} aria-hidden />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <div className="text-muted-foreground absolute top-0 left-0 text-[10px] tabular-nums">
          {format(max)}
        </div>
        <div
          className="border-border flex h-40 items-end gap-[2px] border-b pt-4"
          role="img"
          aria-label={`${label} per ${bucket}`}
          onMouseLeave={() => setHover(null)}
        >
          {data.map((d, i) => (
            <div
              key={d.bucket}
              className="flex h-full min-w-0 flex-1 cursor-default flex-col-reverse gap-[2px]"
              onMouseEnter={() => setHover(i)}
            >
              {segments.map((s) => {
                const v = s.value(d);
                return v > 0 ? (
                  <div
                    key={s.key}
                    className={cn(
                      'w-full first:rounded-b-none last:rounded-t-[4px]',
                      s.className,
                      hover !== null && hover !== i && 'opacity-50',
                    )}
                    style={{ height: `${(v / max) * 100}%` }}
                  />
                ) : null;
              })}
            </div>
          ))}
        </div>
        {hovered && (
          <div
            className="bg-popover text-popover-foreground pointer-events-none absolute top-0 z-10 rounded-md border px-2 py-1 text-xs shadow-md"
            style={{
              left: `${((hover! + 0.5) / data.length) * 100}%`,
              transform: `translateX(${hover! > data.length / 2 ? '-100%' : '0'})`,
            }}
            role="tooltip"
          >
            <div className="font-medium">{bucketLabel(hovered.bucket, bucket)}</div>
            {segments.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5">
                <span className={cn('size-2 rounded-sm', s.className)} aria-hidden />
                {s.label}: <span className="tabular-nums">{format(s.value(hovered))}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-muted-foreground flex justify-between text-[10px]">
        <span>{data[0] && bucketLabel(data[0].bucket, bucket)}</span>
        <span>{data.at(-1) && bucketLabel(data.at(-1)!.bucket, bucket)}</span>
      </div>
    </div>
  );
}

function MetricsContent({ tenantId }: { tenantId: string }) {
  const [range, setRange] = useState<Range>('24h');
  const [agentId, setAgentId] = useState('all');
  // Rounded to the minute so the query key is stable between renders.
  const [now] = useState(() => Math.ceil(Date.now() / 60_000) * 60_000);
  const from = new Date(now - RANGES[range].ms).toISOString();
  const to = new Date(now).toISOString();
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const agentList = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const params = new URLSearchParams({ from, to });
  if (agentId !== 'all') params.set('agentId', agentId);
  const metrics = useQuery({
    queryKey: ['metrics', tenantId, range, agentId, now],
    queryFn: () => api<MetricsDto>(`/metrics?${params.toString()}`),
  });
  const m = metrics.data;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-3">
        <Select value={range} onValueChange={(v) => setRange(v as Range)}>
          <SelectTrigger className="w-44" aria-label="Time range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(RANGES) as Range[]).map((r) => (
              <SelectItem key={r} value={r}>
                {RANGES[r].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={agentId} onValueChange={setAgentId}>
          <SelectTrigger className="w-52" aria-label="Agent">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agents</SelectItem>
            {agentList.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ErrorAlert error={metrics.error} />
      {!m ? (
        <Skeleton className="h-96" />
      ) : m.totals.runs === 0 && m.totals.workflowRuns === 0 ? (
        <EmptyState title="No activity in this range">
          Metrics appear once agents run in the playground, through endpoints or in workflows.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Stat
              label="Runs"
              value={formatNumber(m.totals.runs)}
              hint={`${formatNumber(m.totals.failed)} failed`}
            />
            <Stat label="Error rate" value={formatPercent(m.totals.errorRate)} />
            <Stat label="p50 latency" value={formatDuration(m.totals.latencyMs.p50)} />
            <Stat
              label="p95 latency"
              value={formatDuration(m.totals.latencyMs.p95)}
              hint={`p99 ${formatDuration(m.totals.latencyMs.p99)}`}
            />
            <Stat
              label="Tokens"
              value={formatCompact(m.totals.inputTokens + m.totals.outputTokens)}
              title={`${formatNumber(m.totals.inputTokens + m.totals.outputTokens)} tokens`}
              hint={`${formatCompact(m.totals.inputTokens)} in · ${formatCompact(m.totals.outputTokens)} out`}
            />
            <Stat
              label="Workflow runs"
              value={formatNumber(m.totals.workflowRuns)}
              hint={`${formatNumber(m.totals.toolCalls)} tool calls`}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Runs</CardTitle>
                <CardDescription>Per {m.bucket}, split by outcome.</CardDescription>
              </CardHeader>
              <CardContent>
                <BarChart
                  label="Runs"
                  data={m.series}
                  bucket={m.bucket}
                  format={formatNumber}
                  segments={[
                    {
                      key: 'ok',
                      label: 'Succeeded',
                      className: 'bg-foreground/70',
                      value: (d) => d.runs - d.failed,
                    },
                    {
                      key: 'failed',
                      label: 'Failed',
                      className: 'bg-destructive',
                      value: (d) => d.failed,
                    },
                  ]}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">p95 latency</CardTitle>
                <CardDescription>95th-percentile run duration per {m.bucket}.</CardDescription>
              </CardHeader>
              <CardContent>
                <BarChart
                  label="p95 latency"
                  data={m.series}
                  bucket={m.bucket}
                  format={(n) => formatDuration(n)}
                  segments={[
                    {
                      key: 'p95',
                      label: 'p95',
                      className: 'bg-foreground/70',
                      value: (d) => d.p95LatencyMs ?? 0,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By agent</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead className="text-right">Runs</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                      <TableHead className="text-right">p95</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {m.byAgent.map((a) => (
                      <TableRow key={a.agentId}>
                        <TableCell>
                          <Link
                            to="/agents/$agentId"
                            params={{ agentId: a.agentId }}
                            className="hover:underline"
                          >
                            {a.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(a.runs)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatPercent(a.runs ? a.failed / a.runs : null)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDuration(a.p95LatencyMs)}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={formatNumber(a.tokens)}
                        >
                          {formatCompact(a.tokens)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By model</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Runs</TableHead>
                      <TableHead className="text-right">Avg latency</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {m.byModel.map((x) => (
                      <TableRow key={x.model}>
                        <TableCell className="font-mono text-xs">{x.model}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(x.runs)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDuration(x.avgLatencyMs)}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={formatNumber(x.tokens)}
                        >
                          {formatCompact(x.tokens)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tools</CardTitle>
              </CardHeader>
              <CardContent>
                {m.tools.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No tool calls in this range.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tool</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Failures</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {m.tools.map((t) => (
                        <TableRow key={t.name}>
                          <TableCell className="font-mono text-xs">{t.name}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(t.calls)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(t.failures)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Scores &amp; errors</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                {m.scores.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No scores in this range.{' '}
                    <Link to="/scorers" className="underline">
                      Set up a scorer
                    </Link>
                    .
                  </p>
                ) : (
                  <ul className="grid gap-2 text-sm">
                    {m.scores.map((s) => (
                      <li key={s.scorerId} className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs">{s.name}</span>
                        <span className="flex items-center gap-3 text-xs">
                          <ScoreBar score={s.average} />
                          <span className="text-muted-foreground">{s.count} scores</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {m.errors.length > 0 && (
                  <ul className="grid gap-1 border-t pt-3 text-sm">
                    {m.errors.map((e) => (
                      <li key={e.code} className="flex justify-between">
                        <code className="text-xs">{e.code}</code>
                        <span className="tabular-nums">{formatNumber(e.count)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

export function MetricsPage() {
  return (
    <>
      <PageHeader
        title="Metrics"
        description="Volume, latency percentiles, error rates, tokens, tool usage and evaluation scores."
      />
      <RequireTenant>{(tenant) => <MetricsContent tenantId={tenant.id} />}</RequireTenant>
    </>
  );
}
